import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  QueueListIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { listEvents, getEventStats, type EventData } from '../services/api'
import { dateTime } from '../utils/date'
import {
  EmptyState,
  PageHeader,
  SearchInput,
  Select,
  Spinner,
  StatCard,
  type IconComponent,
} from '../components/ui'

/**
 * Event type -> the label the type filter and each row show, in the order the filter lists them.
 * The first block is the reference's list; the tenant events after it are ours and stay
 * filterable rather than being dropped to match the reference's shorter menu.
 */
const EVENT_TYPES: { value: string; label: string }[] = [
  { value: 'login_success', label: 'Login' },
  { value: 'login_failed', label: 'Login Failed' },
  { value: 'logout', label: 'Logout' },
  { value: 'password_set', label: 'Password Set' },
  { value: 'app_created', label: 'App Published' },
  { value: 'app_updated', label: 'App Updated' },
  { value: 'app_deleted', label: 'App Removed' },
  { value: 'deployment_to_intune', label: 'Deployed to Intune' },
  { value: 'deployment_removed', label: 'Deployment Removed' },
  { value: 'assignment_created', label: 'Assignment Created' },
  { value: 'assignment_deleted', label: 'Assignment Removed' },
  { value: 'job_submitted', label: 'Job Submitted' },
  { value: 'job_completed', label: 'Job Completed' },
  { value: 'job_failed', label: 'Job Failed' },
  { value: 'user_invited', label: 'User Invited' },
  { value: 'user_created', label: 'User Created' },
  { value: 'user_updated', label: 'User Updated' },
  { value: 'user_deleted', label: 'User Removed' },
  { value: 'intune_config_created', label: 'Intune Configured' },
  { value: 'intune_config_updated', label: 'Intune Updated' },
  { value: 'intune_config_deleted', label: 'Intune Removed' },
  { value: 'intune_connection_tested', label: 'Intune Tested' },
  { value: 'tenant_created', label: 'Tenant Added' },
  { value: 'tenant_updated', label: 'Tenant Updated' },
  { value: 'tenant_deleted', label: 'Tenant Removed' },
  { value: 'tenant_suspended', label: 'Tenant Suspended' },
  { value: 'tenant_activated', label: 'Tenant Activated' },
]

const EVENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  EVENT_TYPES.map((t) => [t.value, t.label])
)

/** Severity -> its glyph and the colour of the row's icon and left border. */
const SEVERITY: Record<string, { icon: IconComponent; tone: string }> = {
  success: {
    icon: CheckCircleIcon,
    tone: 'text-emerald-600 dark:text-emerald-400 border-l-emerald-500',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    tone: 'text-amber-600 dark:text-amber-400 border-l-amber-500',
  },
  error: {
    icon: XCircleIcon,
    tone: 'text-red-600 dark:text-red-400 border-l-red-500',
  },
  info: {
    icon: InformationCircleIcon,
    tone: 'text-blue-600 dark:text-blue-400 border-l-blue-500',
  },
}

const SEVERITY_OPTIONS = [
  { value: 'all', label: 'All Severity' },
  { value: 'success', label: 'Success' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'info', label: 'Info' },
]

const PAGE_SIZE = 50

/** Counts across everything the current filters match, not just the page on screen. */
interface SeverityCounts {
  total: number
  success: number
  warning: number
  error: number
  info: number
}

const EMPTY_COUNTS: SeverityCounts = { total: 0, success: 0, warning: 0, error: 0, info: 0 }

export default function Events() {
  const [events, setEvents] = useState<EventData[]>([])
  const [counts, setCounts] = useState<SeverityCounts>(EMPTY_COUNTS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [severity, setSeverity] = useState('all')
  const [eventType, setEventType] = useState('all')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)

  // The reference filters as you type. Ours searches server-side, so hold the keystrokes for a
  // beat rather than firing a request per character.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim())
      setOffset(0)
    }, 300)
    return () => clearTimeout(id)
  }, [searchInput])

  const filters = useMemo(() => {
    const params: {
      severity?: string
      event_type?: string
      search?: string
      date_from?: string
      date_to?: string
    } = {}
    if (severity !== 'all') params.severity = severity
    if (eventType !== 'all') params.event_type = eventType
    if (search) params.search = search
    if (dateFrom) params.date_from = new Date(dateFrom).toISOString()
    if (dateTo) params.date_to = new Date(dateTo).toISOString()
    return params
  }, [severity, eventType, search, dateFrom, dateTo])

  const filtered = Object.keys(filters).length > 0

  // Keeps a slow response from a superseded filter out of the list.
  const requestRef = useRef(0)

  const loadEvents = async () => {
    const request = ++requestRef.current
    setLoading(true)
    setError(null)
    try {
      // The cards count every severity, so they take the filters minus the severity one — picking
      // "Error" must not leave the other three cards reading zero. A backend without the counts
      // leaves the cards on the page's own tally rather than failing the whole load.
      const { severity: _severity, ...statsFilters } = filters
      const [page, stats] = await Promise.all([
        listEvents({ ...filters, limit: PAGE_SIZE, offset }),
        getEventStats(statsFilters).catch(() => null),
      ])
      if (request !== requestRef.current) return
      setEvents(page.events)
      setTotal(page.total)
      setCounts(stats ?? pageCounts(page.events, page.total))
    } catch (err) {
      if (request !== requestRef.current) return
      console.error('Failed to load events:', err)
      setError('Failed to load events. Please try again.')
    } finally {
      if (request === requestRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    loadEvents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, offset])

  const clearFilters = () => {
    setSearchInput('')
    setSearch('')
    setSeverity('all')
    setEventType('all')
    setDateFrom('')
    setDateTo('')
    setOffset(0)
  }

  return (
    <div className="p-8">
      <PageHeader
        title="Events"
        subtitle="Monitor deployment activities and system events"
        actions={
          <>
            {/* The date range sits on the title row rather than in the filter bar: it bounds the
                whole page — the cards as well as the list — where the filters below narrow the
                list alone. */}
            <div className="flex items-center gap-2">
              <label htmlFor="events-from" className="text-xs font-medium text-fmuted">From</label>
              <input
                id="events-from"
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setOffset(0) }}
                className="input w-auto"
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="events-to" className="text-xs font-medium text-fmuted">To</label>
              <input
                id="events-to"
                type="datetime-local"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setOffset(0) }}
                className="input w-auto"
              />
            </div>
            <button onClick={loadEvents} className="btn-ghost">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <SearchInput
          value={searchInput}
          onChange={setSearchInput}
          placeholder="Search events..."
          className="w-full max-w-xs"
        />
        <Select
          className="w-44"
          value={severity}
          onChange={(v) => { setSeverity(v); setOffset(0) }}
          options={SEVERITY_OPTIONS}
        />
        <Select
          className="w-52"
          value={eventType}
          onChange={(v) => { setEventType(v); setOffset(0) }}
          options={[{ value: 'all', label: 'All Types' }, ...EVENT_TYPES]}
        />
        {filtered && (
          <button onClick={clearFilters} className="btn-ghost btn-sm">
            Clear filters
          </button>
        )}
      </div>

      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Total Events" value={counts.total} icon={QueueListIcon} tone="blue" />
        <StatCard label="Successful" value={counts.success} icon={CheckCircleIcon} tone="green" />
        <StatCard label="Warnings" value={counts.warning} icon={ExclamationTriangleIcon} tone="amber" />
        <StatCard label="Errors" value={counts.error} icon={XCircleIcon} tone="red" />
      </div>

      {error && (
        <div className="mb-5 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && events.length === 0 ? (
        <div className="flex justify-center py-24 text-fmuted">
          <Spinner className="h-6 w-6" />
        </div>
      ) : events.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<QueueListIcon className="h-7 w-7" />}
            title={filtered ? 'No events match' : 'No events yet'}
            hint={
              filtered
                ? 'Adjust the filters or search term.'
                : 'Events will appear here as activities occur.'
            }
          />
        </div>
      ) : (
        <>
          <div className="space-y-2.5">
            {events.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </div>

          {total > PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between text-xs text-fmuted">
              <span>
                Showing {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset === 0}
                  className="btn-ghost btn-sm"
                >
                  Previous
                </button>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={offset + PAGE_SIZE >= total}
                  className="btn-ghost btn-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function EventRow({ event }: { event: EventData }) {
  const { icon: Icon, tone } = SEVERITY[event.severity] ?? SEVERITY.info
  const iconColour = tone
    .split(' ')
    .filter((c) => c.includes('text-'))
    .join(' ')

  return (
    <div className={`card border-l-4 p-4 ${tone}`}>
      <div className="flex items-start gap-3">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${iconColour}`} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-semibold text-ftext">
              {event.title}{' '}
              <span className="font-normal text-ffaint">({eventTypeLabel(event.event_type)})</span>
            </p>
            <span className="text-[12px] text-ffaint">{formatTimestamp(event.created_at)}</span>
          </div>
          {event.description && <p className="mt-1 text-sm text-fmuted">{event.description}</p>}
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[12px] text-ffaint">
            {event.user_name && <span>By: {event.user_name}</span>}
            {event.ip_address && <span>IP: {event.ip_address}</span>}
            {event.resource_type && event.resource_name && (
              <span className="font-mono">
                {event.resource_type}: {event.resource_name}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function eventTypeLabel(eventType: string) {
  return (
    EVENT_TYPE_LABELS[eventType] ||
    eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
  )
}

const formatTimestamp = (value: string) => dateTime(value)

function pageCounts(events: EventData[], total: number): SeverityCounts {
  const counts = { ...EMPTY_COUNTS, total }
  events.forEach((e) => {
    if (e.severity in counts) counts[e.severity as keyof SeverityCounts]++
  })
  return counts
}
